import { FilterTypeModel } from './abstract-filter-type.model';
import { FilterOptionModel } from './filter-option.model';
import { FilterTypesEnum } from '../enums/filters-types.enum';
import { ChipModel } from '../../Forms/models/chip.model';
import { FilterQuickOptionModel } from './filter-quick-option.model';
import { IncludeExcludeEnum } from '../enums/include-exclude.enum';
import { clone } from 'underscore';

export class FilterTypeCheckboxModel<T = any> extends FilterTypeModel<T[]> {
    readonly key: FilterTypesEnum = FilterTypesEnum.checkboxes;
    options: FilterOptionModel<T>[];
    showSearch: boolean;
    addCustom: (search: string) => FilterOptionModel<T>;
    ommitChipsIfAllSelected: boolean;
    placeholder: string;
    showFilterLabel: boolean;

    protected value: T[];
    protected currentValue: T[];

    chips: ChipModel[] = [];
    currentChips: ChipModel[] = [];

    constructor(
        id: string,
        label: string,
        options?: FilterOptionModel<T>[],
        showSearch?: boolean,
        addCustom?: (search: string) => FilterOptionModel<T>,
        value?: T[],
        ommitChipsIfAllSelected?: boolean,
        placeholder?: string,
        section?: string,
        showIncludeExclude?: boolean,
        showFilterLabel?: boolean
    ) {
        super(id, label, section, showIncludeExclude);

        this.options = options || [];
        this.showSearch = showSearch || false;
        this.addCustom = addCustom;
        this.ommitChipsIfAllSelected = ommitChipsIfAllSelected;
        this.setValue(value || []);
        this.placeholder = placeholder;
        this.showFilterLabel = showFilterLabel;
    }

    /**
     * Sets the active value for this filter
     * @param value
     */
    setValue(value: T[]): void {
        this.value = value;

        if (this.options) {
            if (!this.ommitChipsIfAllSelected  || this.value.length !== this.options.length) {
                this.chips = this.value?.map((val) => {
                    return clone(this.options.find((option) => option.value === val));
                }).filter(chip => !!chip);
            } else if (this.ommitChipsIfAllSelected && this.value.length === this.options.length) {
                this.chips = [];
            }
        }
        this.cancelChanges();
        this.updateChipSuffix();
    }

    /**
     * Gets the active value for this filter
     */
    getValue(): T[] {
        return this.value;
    }

    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    removeChip(chip: ChipModel): void {
        this.setValue(this.value.filter(valueChip => valueChip !== (<any>chip).value));
    }

    /**
     * Resets the filter to not apply any changes
     */
    clear(): void {
        this.setValue([]);
        this.setIncludeExclude(IncludeExcludeEnum.include);
    }

    /**
     * Sets the current value for a filter
     * @param value
     */
    setCurrentValue(value: T[]): void {
        this.currentValue = value;

        if (this.options && this.currentValue) {
            if (!this.ommitChipsIfAllSelected  || this.currentValue.length !== this.options.length) {
                this.currentChips = this.currentValue.map((val) => {
                    return this.options.find((option) => option.value === val);
                }).filter(chip => !!chip);
            } else if (this.ommitChipsIfAllSelected && this.currentValue.length === this.options.length) {
                this.currentChips = [];
            }
        }

        this.updateCurrentChipSuffix();
    }

    /**
     * Gets the current value for a filter
     */
    getCurrentValue(): T[] {
        return this.currentValue;
    }

    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    removeCurrentChip(chip: ChipModel): void {
        this.setCurrentValue(this.currentValue.filter(valueChip => valueChip !== (<any>chip).value));
    }

    /**
     * Resets the current value to the active value
     */
    cancelChanges(): void {
        this.setCurrentValue(this.value?.slice());
    }

    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel<T>[] {
        const queryLower = query.toLocaleLowerCase();
        const matchedOptions = this.options.filter(
            opt => {
                if (this.value.includes(opt.value)) {
                    return false;
                }
                return translateMethod(opt.label).toLocaleLowerCase().includes(queryLower);
            }
        );

        return matchedOptions.map(opt => {
            return {
                label: opt.label,
                value: opt.value,
                count: opt.count,
                filter: this
            };
        });
    }
    addQuickOption(option: FilterQuickOptionModel<T>): void {
        this.setValue([...this.value, option.value]);
    }
}
